import type { History, Location } from 'history'
import React from 'react'
import { useDispatch } from 'react-redux'
import { Prompt } from 'react-router-dom'
import { useNavigate } from 'react-router-dom-v5-compat'

import Loader from '@/app/component/atom/loader'
import { useConfirmation } from '@/app/contexts/confirmation.context'
import { useGetCampaign } from '@/app/module/campaigns/hooks/use-get-campaign'
import { useGetCampaignFile } from '@/app/module/campaigns/hooks/use-get-campaign-file'
import { useSaveCampaign } from '@/app/module/campaigns/hooks/use-save-campaign'
import { useSaveCampaignFile } from '@/app/module/campaigns/hooks/use-save-campaign-file'
import { resetDidChange } from '@/app/module/campaigns/store/slices/campaign-item.slice'
import { CampaignTemplateType } from '@/app/module/campaigns/types'

import CampaignFilesProvider, { useCampaignFiles } from './campaign-files-provider'
import { getUnsavedChangesConfirmation } from './confirmations'
import Item, { type SaveChangesRef } from './item'
import ChatbotEditor from './item/chatbot'
import { isCampaignEditPath } from './step-helpers'

type Props = {
  changed: boolean
  history: History
  itemId: number | string
  step?: string
  type: CampaignTemplateType

  onFinish: (campaignId?: number | string) => void
  onSave: (payload: { id: number | string; tab?: string }) => void
}

type EditorProps = Props & Pick<ReturnType<typeof useGetCampaign>, 'data' | 'isFetching'>

const CampaignEditor: React.FC<EditorProps> = (props) => {
  const { changed, data: campaign, isFetching, itemId } = props
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const createConfirmation = useConfirmation()
  const [exitLocation, setExitLocation] = React.useState<Location>()
  const [confirmationPending, setConfirmationPending] = React.useState(false)
  const saveChangesRef: SaveChangesRef = React.useRef(null)
  const { files } = useCampaignFiles()
  const [getFile] = useGetCampaignFile()
  const [saveCampaign, { isLoading }] = useSaveCampaign()
  const [saveFile] = useSaveCampaignFile()

  const loading = isFetching || isLoading

  React.useEffect(() => {
    if (!changed) return
    const handleUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault()
      event.returnValue = ''
    }
    window.addEventListener('beforeunload', handleUnload)
    return () => window.removeEventListener('beforeunload', handleUnload)
  }, [changed])

  React.useEffect(() => {
    if (!exitLocation) return
    navigate(exitLocation, { state: exitLocation.state })
    setExitLocation(undefined)
  }, [exitLocation, navigate])

  const exitCampaign = (destination: Location) => {
    dispatch(resetDidChange())
    setExitLocation(destination)
  }

  const confirmExit = async (destination: Location) => {
    setConfirmationPending(true)
    try {
      const confirmed = await createConfirmation(getUnsavedChangesConfirmation(() => exitCampaign(destination)))
      if (confirmed && (await saveChangesRef.current?.())) exitCampaign(destination)
    } finally {
      setConfirmationPending(false)
    }
  }

  const handleBlockedNavigation = (destination: Location) => {
    if (confirmationPending) return false
    if (!changed || isCampaignEditPath(destination)) return true
    if (isLoading) return false
    confirmExit(destination)
    return false
  }

  if (itemId && !campaign) {
    return <Loader />
  }

  if (campaign?.type === CampaignTemplateType.Chatbot) {
    return <ChatbotEditor campaignId={itemId} />
  }

  return (
    <>
      <Prompt when={(changed || isLoading) && !exitLocation} message={handleBlockedNavigation} />
      <Item
        {...props}
        files={files}
        item={campaign}
        loading={loading}
        saveChangesRef={saveChangesRef}
        getFile={getFile}
        saveCampaign={saveCampaign}
        saveFile={saveFile}
      />
    </>
  )
}

const ItemContainer: React.FC<Props> = (props) => {
  const { data, files, isFetching } = useGetCampaign(props.itemId)
  return (
    <CampaignFilesProvider campaignId={props.itemId} initialFiles={files}>
      <CampaignEditor {...props} data={data} isFetching={isFetching} />
    </CampaignFilesProvider>
  )
}

export default ItemContainer
